// Store
import { useTemplateStore } from "@/zustand/templateStore";
import { useProductData } from "@/zustand/productStore";
import { useCartStore } from "@/zustand/cartStore";

// Utils
import captureException from "@/func/sentryException";
import {
  buildApplePayCustomer,
  missingShippingFields,
  missingAddressError,
} from "@/utils/applePayContact";

// Assets
import applePay from "@/assets/applePay.svg";

// Re-exported so consumers keep importing the payload shape from the component
// that produces it, while `applePayContact` stays the single source of truth.
export type {
  ApplePayCustomer,
  ApplePayCustomerAddress,
} from "@/utils/applePayContact";

import type { ApplePayCustomer } from "@/utils/applePayContact";

export interface ApplePayData {
  apple_pay_token: any;
  customer: ApplePayCustomer;
}

interface ApplePayButtonProps {
  onApplePaySubmit?: (
    data: ApplePayData,
    options?: { signal: AbortSignal },
  ) => Promise<void>;
  height?: number;
  className?: string;
}

const ApplePayButton: React.FC<ApplePayButtonProps> = ({
  onApplePaySubmit,
  height,
  className,
}) => {
  const handlePaymentMethodChange = useTemplateStore(
    (s) => s.handlePaymentMethodChange,
  );

  const isApplePayAvailable = useTemplateStore((s) => s.isApplePayAvailable);
  const { currency_code, country_code, paymentsConfig } =
    useProductData() as any;
  const { dynamicDescriptor } = paymentsConfig || {};
  const { cartTotal } = useCartStore();

  const startApplePay = async () => {
    handlePaymentMethodChange("applepay");

    const paymentRequest = {
      currencyCode: currency_code || "USD",
      countryCode: country_code || "US",
      total: {
        label: dynamicDescriptor,
        amount: cartTotal || "0.00",
      },
      supportedNetworks: ["visa", "masterCard", "amex", "discover", "JCB"],
      merchantCapabilities: ["supports3DS"],
      requiredShippingContactFields: [
        "email",
        "phone",
        "postalAddress",
        "name",
      ],
    };

    const session = new ApplePaySession(3, paymentRequest);

    session.onvalidatemerchant = async () => {
      const r = await fetch("/wp-json/checkout/v1/order/applepay/validate", {
        method: "POST",
      });
      const merchantSession = await r.json();
      session.completeMerchantValidation(merchantSession);
    };

    session.oncancel = () => {
      // TODO: Add cancel handler
    };

    session.onpaymentauthorized = async (
      event: ApplePayJS.ApplePayPaymentAuthorizedEvent,
    ) => {
      try {
        const controller = new AbortController();
        const timeout = setTimeout(() => controller.abort(), 30000);

        const customer = buildApplePayCustomer(event.payment);
        const missing = missingShippingFields(customer);

        // Never charge for an order we cannot ship. Returning errors keeps the
        // Apple Pay sheet open so the customer can correct their address.
        if (missing.length > 0) {
          clearTimeout(timeout);
          captureException(
            new Error(
              `Apple Pay contact missing required fields: ${missing.join(", ")}`,
            ),
            {
              tags: {
                type: "Javascript error",
                error: "apple_pay_incomplete_shipping_contact",
              },
              extra: { missing_fields: missing },
            },
          );
          session.completePayment({
            status: window.ApplePaySession.STATUS_FAILURE,
            errors: [missingAddressError()],
          });
          return;
        }

        const applePayData: ApplePayData = {
          apple_pay_token: event.payment.token,
          customer,
        };

        if (onApplePaySubmit) {
          await onApplePaySubmit(applePayData, { signal: controller.signal });
          clearTimeout(timeout);
          session.completePayment(window.ApplePaySession.STATUS_SUCCESS);
        } else {
          session.completePayment(window.ApplePaySession.STATUS_FAILURE);
        }
      } catch (e) {
        console.error("Payment authorization error:", e);
        session.completePayment(window.ApplePaySession.STATUS_FAILURE);
      }
    };

    session.begin();
  };

  if (!isApplePayAvailable) {
    return null;
  }

  return (
    <button
      className={className}
      onClick={startApplePay}
      formNoValidate
      type="button"
    >
      <img
        src={applePay}
        alt="Apple Pay"
        className={`mx-auto md:max-w-none ${height ? `h-[${height}px]` : "h-8"}`}
      />
    </button>
  );
};

export default ApplePayButton;
